/**
 * #53 / DEC-360 — the signed-in user's Map View layer switches, remembered per user in
 * this browser (map-layer-prefs.ts). Starts all-off until the user is known, then loads
 * their stored choice; switching user (sign-out/in on a shared PC) reloads it.
 */
import * as React from 'react';
import { useAuth } from '@lodex-pro/web-shared';
import { DEFAULT_LAYER_PREFS, type MapLayerPrefs } from './map-layers.js';
import { readLayerPrefs, writeLayerPrefs } from './map-layer-prefs.js';

export function useMapLayerPrefs(): {
  readonly prefs: MapLayerPrefs;
  readonly setLayer: (which: keyof MapLayerPrefs, on: boolean) => void;
} {
  const { user } = useAuth();
  const userId = user?.userId ?? null;
  const [prefs, setPrefs] = React.useState<MapLayerPrefs>(DEFAULT_LAYER_PREFS);

  React.useEffect(() => {
    setPrefs(readLayerPrefs(userId));
  }, [userId]);

  const setLayer = React.useCallback(
    (which: keyof MapLayerPrefs, on: boolean): void => {
      setPrefs((prev) => {
        const next = { ...prev, [which]: on };
        writeLayerPrefs(userId, next);
        return next;
      });
    },
    [userId],
  );

  return { prefs, setLayer };
}
